import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Utensils, Mail, Lock, ArrowRight, Phone, X, ArrowLeft } from "lucide-react";

const QUICK_LOGINS = [
  { label: "Customer", email: "customer@eatup.com", password: "Customer@123", color: "bg-eatup-green" },
  { label: "Driver", email: "driver@eatup.com", password: "Driver@123", color: "bg-eatup-charcoal" },
  { label: "Restaurant", email: "restaurant@eatup.com", password: "Restaurant@123", color: "bg-eatup-yellow text-eatup-charcoal" },
  { label: "Admin", email: "admin@eatup.com", password: "Admin@123", color: "bg-neutral-700" },
];

export default function LoginPage() {
  const { login, startGoogleLogin } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [phoneOpen, setPhoneOpen] = useState(false);

  const routeForRole = (role) => {
    if (role === "driver") return "/driver";
    if (role === "restaurant") return "/restaurant-admin";
    if (role === "admin") return "/admin";
    return "/home";
  };

  const submit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name.split(" ")[0]}!`);
      navigate(routeForRole(u.role));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const quickLogin = async (email, password) => {
    setEmail(email); setPassword(password);
    setLoading(true);
    try {
      const u = await login(email, password);
      navigate(routeForRole(u.role));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  };

  const oauthErr = params.get("err") === "oauth";

  return (
    <div className="min-h-screen w-full grid lg:grid-cols-2 bg-white">
      {/* Left hero */}
      <div className="relative hidden lg:block overflow-hidden">
        <img
          src="https://images.pexels.com/photos/27556985/pexels-photo-27556985.jpeg"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-eatup-charcoal/85 via-eatup-charcoal/30 to-transparent" />
        <div className="relative z-10 h-full flex flex-col justify-between p-14 text-white">
          <div className="flex items-center gap-3">
            <img src="/eats-gnd-logo.png" alt="Eats GND" className="w-14 h-14 rounded-2xl object-cover shadow-lg ring-2 ring-white/30" draggable={false} />
            <span className="font-display text-2xl font-bold">Eats <span className="text-eatup-green">GND</span></span>
          </div>
          <div>
            <div className="uppercase tracking-[0.35em] text-xs text-eatup-yellow font-bold mb-4">Caribbean · Curated</div>
            <h2 className="font-display text-5xl font-bold leading-tight max-w-md">
              Good food.<br />Delivered to your door.
            </h2>
            <p className="mt-4 text-white/80 max-w-md">
              Jerk chicken, oil down, curry goat and more — from the island's most beloved kitchens.
            </p>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-eatup-gray">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <img src="/eats-gnd-logo.png" alt="Eats GND" className="w-11 h-11 rounded-2xl object-cover shadow-md" draggable={false} />
            <span className="font-display text-2xl font-bold">Eats <span className="text-eatup-green">GND</span></span>
          </div>

          <h1 className="font-display text-4xl font-bold text-eatup-charcoal">Welcome back</h1>
          <p className="mt-2 text-neutral-500">Sign in to keep eating well.</p>

          {oauthErr && (
            <div className="mt-4 rounded-xl bg-red-50 border border-red-100 text-red-700 text-sm px-4 py-3">
              Google sign-in failed. Please try again.
            </div>
          )}

          <form onSubmit={submit} className="mt-8 space-y-4">
            <label className="block">
              <span className="text-xs font-semibold text-neutral-600 uppercase tracking-wider">Email</span>
              <div className="mt-1 relative">
                <Mail className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
                <input
                  data-testid="login-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@island.com"
                  className="w-full pl-11 pr-4 py-3.5 rounded-2xl bg-white border border-neutral-200 focus:border-eatup-green focus:ring-4 focus:ring-eatup-green/10 outline-none transition"
                />
              </div>
            </label>
            <label className="block">
              <span className="text-xs font-semibold text-neutral-600 uppercase tracking-wider">Password</span>
              <div className="mt-1 relative">
                <Lock className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
                <input
                  data-testid="login-password-input"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full pl-11 pr-4 py-3.5 rounded-2xl bg-white border border-neutral-200 focus:border-eatup-green focus:ring-4 focus:ring-eatup-green/10 outline-none transition"
                />
              </div>
            </label>

            <button
              type="submit"
              data-testid="login-submit-btn"
              disabled={loading}
              className="w-full btn-primary text-base disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {loading ? "Signing in…" : "Sign in"}
              {!loading && <ArrowRight className="w-4 h-4" />}
            </button>
          </form>

          <div className="my-6 flex items-center gap-3 text-xs text-neutral-400">
            <div className="flex-1 h-px bg-neutral-200" />
            <span>OR</span>
            <div className="flex-1 h-px bg-neutral-200" />
          </div>

          <button
            onClick={startGoogleLogin}
            data-testid="google-login-btn"
            className="w-full flex items-center justify-center gap-3 py-3.5 rounded-2xl bg-white border border-neutral-200 hover:border-neutral-300 transition font-medium"
          >
            <svg viewBox="0 0 24 24" className="w-5 h-5">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </button>

          <button
            onClick={() => setPhoneOpen(true)}
            data-testid="phone-login-btn"
            className="mt-3 w-full flex items-center justify-center gap-3 py-3.5 rounded-2xl bg-white border border-neutral-200 hover:border-neutral-300 transition font-medium"
          >
            <Phone className="w-4 h-4 text-eatup-green" />
            Continue with phone number
          </button>

          <div className="mt-6">
            <div className="text-xs font-semibold text-neutral-500 uppercase tracking-wider mb-2">Try a demo account</div>
            <div className="grid grid-cols-2 gap-2">
              {QUICK_LOGINS.map((q) => (
                <button
                  key={q.label}
                  data-testid={`quick-login-${q.label.toLowerCase()}`}
                  onClick={() => quickLogin(q.email, q.password)}
                  className={`${q.color} text-white text-sm font-semibold py-2.5 rounded-xl hover:opacity-90 transition`}
                >
                  {q.label}
                </button>
              ))}
            </div>
          </div>

          <p className="mt-8 text-sm text-neutral-500 text-center">
            New to Eats GND?{" "}
            <Link to="/register" data-testid="go-to-register-link" className="text-eatup-green font-semibold hover:underline">
              Create an account
            </Link>
          </p>
        </div>
      </div>

      {phoneOpen && (
        <PhoneLoginModal
          onClose={() => setPhoneOpen(false)}
          onSuccess={(user) => {
            setPhoneOpen(false);
            toast.success(`Welcome, ${user.name.split(" ")[0]}!`);
            navigate(routeForRole(user.role));
          }}
        />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Phone-OTP login modal — two-step flow: enter phone → enter 6-digit code.
// Auto-registers a customer account on first successful verification.
// -----------------------------------------------------------------------------
function PhoneLoginModal({ onClose, onSuccess }) {
  const { startPhoneLogin, verifyPhoneLogin } = useAuth();
  const [step, setStep] = useState("phone"); // "phone" | "code"
  const [phoneInput, setPhoneInput] = useState("+1473");
  const [challengeId, setChallengeId] = useState(null);
  const [phoneServer, setPhoneServer] = useState("");
  const [devMode, setDevMode] = useState(false);
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const sendCode = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const data = await startPhoneLogin(phoneInput);
      setChallengeId(data.challenge_id);
      setPhoneServer(data.phone);
      setDevMode(!!data.dev_mode);
      setStep("code");
      setCode("");
      setResendCooldown(30);
      toast.success(
        data.dev_mode
          ? "Dev mode — use code 123456"
          : `Code sent to ${data.phone}`
      );
      const timer = setInterval(() => {
        setResendCooldown((s) => {
          if (s <= 1) { clearInterval(timer); return 0; }
          return s - 1;
        });
      }, 1000);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    if (busy || !/^[0-9]{6}$/.test(code)) {
      toast.error("Enter the 6-digit code");
      return;
    }
    setBusy(true);
    try {
      const data = await verifyPhoneLogin({
        challenge_id: challengeId,
        code,
        name: name.trim() || undefined,
      });
      onSuccess(data.user);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="phone-login-modal"
      className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 px-5 py-4 border-b border-neutral-100">
          {step === "code" && (
            <button
              data-testid="phone-back-btn"
              onClick={() => { setStep("phone"); setCode(""); }}
              className="p-1.5 rounded-full hover:bg-neutral-100"
              aria-label="Back"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
          )}
          <div className="flex-1">
            <div className="text-[10px] font-bold uppercase tracking-widest text-eatup-green">
              Phone sign in
            </div>
            <div className="font-display text-xl font-bold text-eatup-charcoal">
              {step === "phone" ? "Enter your phone number" : "Enter the code"}
            </div>
          </div>
          <button
            data-testid="phone-modal-close-btn"
            onClick={onClose}
            className="p-2 rounded-full hover:bg-neutral-100"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {step === "phone" ? (
            <>
              <p className="text-sm text-neutral-500">
                We'll text you a 6-digit code. New here? An account will be created
                automatically after verifying.
              </p>
              <label className="block">
                <span className="text-xs font-semibold text-neutral-600 uppercase tracking-wider">
                  Phone number
                </span>
                <div className="mt-1 relative">
                  <Phone className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
                  <input
                    data-testid="phone-input"
                    type="tel"
                    inputMode="tel"
                    autoFocus
                    value={phoneInput}
                    onChange={(e) => setPhoneInput(e.target.value)}
                    placeholder="+14735551234"
                    className="w-full pl-11 pr-4 py-3.5 rounded-2xl bg-white border border-neutral-200 focus:border-eatup-green focus:ring-4 focus:ring-eatup-green/10 outline-none transition"
                    onKeyDown={(e) => { if (e.key === "Enter") sendCode(); }}
                  />
                </div>
                <span className="mt-1.5 block text-[11px] text-neutral-400">
                  Use full international format. Grenada local 7-digit numbers get +1473 prefixed automatically.
                </span>
              </label>
              <button
                data-testid="phone-send-code-btn"
                onClick={sendCode}
                disabled={busy}
                className="w-full btn-primary text-base disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {busy ? "Sending…" : "Send code"}
                {!busy && <ArrowRight className="w-4 h-4" />}
              </button>
            </>
          ) : (
            <>
              <p className="text-sm text-neutral-500">
                We sent a 6-digit code to <span className="font-semibold text-eatup-charcoal">{phoneServer}</span>.
                {devMode && (
                  <span className="block mt-1 text-xs bg-yellow-50 border border-yellow-200 rounded-lg px-2.5 py-1.5 text-yellow-800">
                    <strong>Dev mode:</strong> Sinch key not configured — use <span className="font-mono">123456</span>.
                  </span>
                )}
              </p>

              <label className="block">
                <span className="text-xs font-semibold text-neutral-600 uppercase tracking-wider">
                  6-digit code
                </span>
                <input
                  data-testid="phone-code-input"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  autoFocus
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  placeholder="123456"
                  className="mt-1 w-full px-4 py-4 rounded-2xl bg-white border border-neutral-200 focus:border-eatup-green focus:ring-4 focus:ring-eatup-green/10 outline-none transition text-center text-3xl font-mono tracking-[0.5em] font-bold"
                  onKeyDown={(e) => { if (e.key === "Enter") verify(); }}
                />
              </label>

              <label className="block">
                <span className="text-xs font-semibold text-neutral-600 uppercase tracking-wider">
                  Your name <span className="text-neutral-400 font-normal normal-case">(optional — first-time only)</span>
                </span>
                <input
                  data-testid="phone-name-input"
                  type="text"
                  maxLength={80}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  className="mt-1 w-full px-4 py-3.5 rounded-2xl bg-white border border-neutral-200 focus:border-eatup-green focus:ring-4 focus:ring-eatup-green/10 outline-none transition"
                />
              </label>

              <button
                data-testid="phone-verify-btn"
                onClick={verify}
                disabled={busy || code.length !== 6}
                className="w-full btn-primary text-base disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {busy ? "Verifying…" : "Verify & sign in"}
                {!busy && <ArrowRight className="w-4 h-4" />}
              </button>

              <button
                data-testid="phone-resend-btn"
                onClick={sendCode}
                disabled={busy || resendCooldown > 0}
                className="w-full text-sm text-neutral-500 hover:text-eatup-green disabled:opacity-60 py-2"
              >
                {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend code"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
